// src/pages/StudentDashboard.jsx
import { useState, useEffect, useMemo } from "react";
import { useAuth } from "../context/AuthContext"; // Import useAuth hook
import { getStudentCourseProgress, getStudent } from "../firebase/database";
import { theme } from "../utils/theme";
import LogTimelineReport from "./LogTimelineReport";
import DietAnalysis from "./DietAnalysis";
import CompleteProfile from "./CompleteProfile";
import StudentProgressCheckup from "./StudentProgressCheckup";
import AIChat from "../components/AIChat";
import TypingTutor from "./TypingTutor";
import AIAssistant from "../components/AIAssistant"; // Import AIAssistant component

const C = theme.colors;

const TABS = {
  COURSES: "courses",
  SELF_LOG: "self_log",
  PROGRESS: "log_timeline", // Renamed from "progress"
  DIET: "diet_timeline",     // Renamed from "diet"
  AI: "life_ai_assistant",   // Renamed from "life_ai" to match user request and intent
  TYPING: "typing_tutor",
  PROFILE: "profile"
};

export default function StudentDashboard({ studentId, onBack, fullScreen = false }) {
  const { user, userData: authUserData, profile: authProfile, role: authRole, logout } = useAuth(); // Destructure with aliases
  const [kioskProfile, setKioskProfile] = useState(null);
  
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCourseId, setSelectedCourseId] = useState("");
  const [dataReady, setDataReady] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [lessonIndex, setLessonIndex] = useState(0);
  
  const [activeTab, setActiveTab] = useState(TABS.COURSES);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Fetch Kiosk profile if not logged in
  useEffect(() => {
    if (!authProfile && studentId) {
      getStudent(studentId).then(setKioskProfile);
    }
  }, [authProfile, studentId]);

  // Derived data for sidebar
  const profile = authProfile || kioskProfile;
  const userData = authUserData || (kioskProfile ? { name: kioskProfile.name } : null);
  const role = authRole || (kioskProfile?.role || "student");

  useEffect(() => {
    loadCourses();
  }, [studentId]);

  const loadCourses = async () => {
    try {
      setLoading(true);
      const progress = await getStudentCourseProgress(studentId);
      setCourses(progress || []);
      setDataReady(true);
    } catch (err) {
      console.error("Error loading courses:", err);
    } finally {
      setLoading(false);
    }
  };

  const selectedCourse = useMemo(() => {
    if (!selectedCourseId || !courses.length) return null;
    return courses.find(c => c.courseId === selectedCourseId) || null;
  }, [courses, selectedCourseId]);

  const formatDate = (iso) => {
    if (!iso) return "";
    try {
      const date = new Date(iso);
      return date.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
    } catch (e) {
      return iso;
    }
  };

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const renderContent = () => {
    switch (activeTab) {
      case TABS.COURSES:
        return (
          <>
            {/* Render AI Assistant insights here when on the Courses tab (dashboard home) */}
            <AIAssistant /> 
            {renderCourses()} {/* Render the course list/details */}
          </>
        );
      case TABS.SELF_LOG:
        return (
          <div style={{ background: "#fff", borderRadius: 16, padding: 24, boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
            <StudentProgressCheckup studentId={studentId} />
          </div>
        );
      case TABS.PROGRESS:
        return (
          <div style={{ padding: 24, background: "#fff", borderRadius: 16, boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
             <LogTimelineReport studentId={studentId} />
          </div>
        );
      case TABS.DIET:
        return (
          <div style={{ padding: 24, background: "#fff", borderRadius: 16, boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
            <DietAnalysis studentId={studentId} />
          </div>
        );
      case TABS.AI:
        return (
          <div style={{ height: "calc(100vh - 140px)", background: "#fff", borderRadius: 16, overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
            <AIChat isPublic={false} /> {/* Rendering AIChat for the AI Assistant page */} 
          </div>
        );
      case TABS.TYPING:
        return (
          <div style={{ padding: 24, background: "#fff", borderRadius: 16, boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
            <TypingTutor externalId={studentId} />
          </div>
        );
      case TABS.PROFILE:
        return (
          <div style={{ height: "calc(100vh - 80px)", overflowY: "auto" }}>
            <CompleteProfile studentId={studentId} isEmbedded={true} />
          </div>
        );
      default:
        return renderCourses();
    }
  };

  const renderCourses = () => {
    if (loading || !dataReady) {
      return (
        <div style={{ textAlign: "center", padding: 40, background: "white", borderRadius: 16, color: "#6B7280" }}>
          <div className="spinner" />
          Loading your courses...
        </div>
      );
    }

    if (courses.length === 0) {
      return (
        <div style={{ textAlign: "center", padding: 40, background: "white", borderRadius: 16 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📚</div>
          <div style={{ fontSize: 20, fontWeight: 600, color: "#111827", marginBottom: 8 }}>No Courses Enrolled</div>
          <div style={{ fontSize: 14, color: "#6B7280" }}>Contact your administrator to enroll in courses.</div>
        </div>
      );
    }

    if (!selectedCourseId) {
      return (
        <div style={{ background: "white", padding: 24, borderRadius: 16, boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: "#111827", marginBottom: 24 }}>My Courses</h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {courses.map((enrollment) => (
              <div
                key={enrollment.courseId}
                onClick={() => setSelectedCourseId(enrollment.courseId)}
                className="course-card"
                style={{
                  background: "#F9FAFB",
                  border: "1px solid #E5E7EB",
                  borderRadius: 12,
                  padding: 20,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <h3 style={{ fontSize: 18, fontWeight: 600, color: "#111827", margin: "0 0 8px 0" }}>{enrollment.courseName}</h3>
                    <div style={{ fontSize: 13, color: "#6B7280" }}>{enrollment.lessons?.length || 0} lessons</div>
                  </div>
                  <div style={{
                    padding: "6px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                    background: enrollment.feePaid ? "#D1FAE5" : "#FEE2E2",
                    color: enrollment.feePaid ? "#047857" : "#DC2626",
                  }}>
                    {enrollment.feePaid ? "✓ Fee Paid" : "⚠ Fee Pending"}
                  </div>
                </div>
                <div style={{ marginTop: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#6B7280", marginBottom: 6 }}>
                    <span>Progress</span>
                    <span>{enrollment.completedLessons?.length || 0} / {enrollment.lessons?.length || 0}</span>
                  </div>
                  <div style={{ height: 6, background: "#E5E7EB", borderRadius: 3, overflow: "hidden" }}>
                    <div style={{
                      height: "100%",
                      width: `${((enrollment.completedLessons?.length || 0) / (enrollment.lessons?.length || 1)) * 100}%`,
                      background: "#3D7EFF",
                      borderRadius: 3,
                    }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    // Lessons list for selected course
    const enrollment = selectedCourse;
    const lessons = enrollment.lessons || [];
    const completedLessons = enrollment.completedLessons || [];
    const completedIds = completedLessons.map(l => l.lessonId);
    const currentIndex = enrollment.currentLessonIndex || 0;

    return (
      <div style={{ background: "white", padding: 24, borderRadius: 16, boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
        <button onClick={() => setSelectedCourseId("")} style={{ background: "none", border: "none", color: "#6B7280", fontSize: 14, cursor: "pointer", marginBottom: 20 }}>← Back to Courses</button>
        <div style={{ background: "linear-gradient(135deg, #EBF1FF 0%, #FFFFFF 100%)", borderRadius: 12, padding: 20, marginBottom: 24, border: "1px solid #EBF1FF" }}>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: "#111827", margin: "0 0 8px 0" }}>{enrollment.courseName}</h2>
          <div style={{ display: "flex", gap: 20, fontSize: 13, color: "#6B7280" }}>
            <span>{lessons.length} Lessons</span>
            <span>{completedLessons.length} Completed</span>
            <span style={{ color: enrollment.feePaid ? "#047857" : "#DC2626", fontWeight: 600 }}>{enrollment.feePaid ? "✓ Fee Paid" : "⚠ Fee Pending"}</span>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {lessons.map((lesson, index) => {
            const isCompleted = completedIds.includes(lesson.id);
            const isLocked = !enrollment.feePaid || (index > currentIndex);
            if (index > currentIndex + 3) return null;
            const completionInfo = completedLessons.find(l => l.lessonId === lesson.id);

            return (
              <div
                key={lesson.id}
                onClick={() => {
                  if (isLocked) return alert("🔒 This lesson is locked.");
                  setSelectedLesson(lesson);
                  setLessonIndex(index);
                }}
                style={{
                  background: isCompleted ? "#F0FDF4" : isLocked ? "#F9FAFB" : "white",
                  border: `1px solid ${isCompleted ? "#BBF7D0" : isLocked ? "#E5E7EB" : "#F3F4F6"}`,
                  borderRadius: 10, padding: 16, opacity: isLocked ? 0.7 : 1, cursor: isLocked ? "not-allowed" : "pointer"
                }}
              >
                <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
                  <div style={{ width: 36, height: 36, borderRadius: "50%", background: isCompleted ? "#16A34A" : isLocked ? "#E5E7EB" : "#3D7EFF", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 600 }}>
                    {isCompleted ? "✓" : isLocked ? "🔒" : index + 1}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
                      <h4 style={{ fontSize: 16, fontWeight: 600, color: isLocked ? "#9CA3AF" : "#111827", margin: 0 }}>{lesson.title}</h4>
                      <span style={{ padding: "2px 10px", borderRadius: 12, fontSize: 11, fontWeight: 500, background: isLocked ? "#F3F4F6" : "#DBEAFE", color: isLocked ? "#9CA3AF" : "#1D4ED8" }}>{lesson.category}</span>
                    </div>
                    {isCompleted && completionInfo && (
                      <div style={{ fontSize: 12, color: "#16A34A" }}>✓ Completed on {formatDate(completionInfo.completedAt)}</div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div style={{ 
      position: fullScreen ? "fixed" : "relative", 
      inset: fullScreen ? 0 : "auto", 
      background: "#F7F8FC", 
      fontFamily: "DM Sans, sans-serif",
      display: "flex",
      zIndex: 1000
    }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .spinner { width: 40px; height: 40px; border: 3px solid #f3f3f3; border-top: 3px solid #3D7EFF; border-radius: 50%; margin: 0 auto 16px; animation: spin 1s linear infinite; }
        .course-card:hover { border-color: #3D7EFF !important; transform: translateY(-2px); }
        
        .sidebar { width: 220px; /* Reduced width */ background: white; border-right: 1px solid #E5E7EB; display: flex; flex-direction: column; height: 100vh; position: sticky; top: 0; }
        .menu-item { 
          padding: 10px 16px; /* Reduced padding */
          display: flex; 
          align-items: center; 
          gap: 10px; /* Adjusted gap */
          cursor: pointer; 
          transition: all 0.2s; 
          border-radius: 8px; /* Slightly reduced radius */
          margin: 2px 12px; 
          color: #6B7280; 
          font-weight: 600; 
          font-size: 14px; /* Reduced font size */
        }
        .menu-item.active { background: #3D7EFF15; color: #3D7EFF; }
        .menu-item:hover:not(.active) { background: #F3F4F6; color: #111827; }
        
        .hamburger-btn { display: none; background: #F3F4F6; border: none; width: 40px; height: 40px; border-radius: 10px; font-size: 18px; cursor: pointer; color: #111827; align-items: center; justify-content: center; }
        
        @media (max-width: 1024px) {
          .sidebar { display: none; }
          .hamburger-btn { display: flex; }
          .main-content { padding: 16px !important; }
          .drawer { width: 280px; } /* Keep drawer width consistent with sidebar */
        }

        .drawer-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 2000; opacity: 0; pointer-events: none; transition: opacity 0.3s; backdrop-filter: blur(2px); }
        .drawer-overlay.open { opacity: 1; pointer-events: auto; }
        .drawer { 
          position: fixed; top: 0; left: -280px; bottom: 0; width: 280px; background: white; z-index: 2001; 
          transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1); display: flex; flex-direction: column; box-shadow: 10px 0 30px rgba(0,0,0,0.1);
        }
        .drawer.open { transform: translateX(280px); }
      `}</style>

      {/* Responsive Sidebar (Desktop Only) */}
      <aside className="sidebar">
        <SidebarContent 
          activeTab={activeTab} 
          setActiveTab={setActiveTab} 
          onBack={onBack} 
          TABS={TABS} 
          user={user}
          userData={userData}
          profile={profile}
          role={role}
        />
      </aside>

      {/* Mobile Drawer */}
      <div className={`drawer-overlay ${isMenuOpen ? "open" : ""}`} onClick={toggleMenu} />
      <div className={`drawer ${isMenuOpen ? "open" : ""}`}>
        <SidebarContent 
          activeTab={activeTab} 
          setActiveTab={setActiveTab} 
          onBack={() => { onBack(); toggleMenu(); }} 
          TABS={TABS} 
          isDrawer={true}
          toggleMenu={toggleMenu}
          user={user}
          userData={userData}
          profile={profile}
          role={role}
        />
      </div>

      {/* Main Container */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: "100vh", overflowX: "hidden" }}>
        {/* Header */}
        <header style={{ height: 72, background: "white", borderBottom: "1px solid #E5E7EB", display: "flex", alignItems: "center", padding: "0 24px", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <button className="hamburger-btn" onClick={toggleMenu}>☰</button>
            <div>
              <h1 style={{ fontSize: 18, fontWeight: 800, color: "#111827", letterSpacing: -0.5, margin: 0 }}>STUDENT PORTAL</h1>
              <div style={{ fontSize: 11, color: "#6B7280", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>{activeTab.replace("_", " ")}</div> {/* Updated to handle new tab names */}
            </div>
          </div>
          <button onClick={onBack} style={{ padding: "10px 18px", borderRadius: 10, background: "#FEE2E2", color: "#DC2626", border: "none", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Finish Session</button>
        </header>

        {/* Content Area */}
        <main className="main-content" style={{ flex: 1, padding: 32, maxWidth: 1100, margin: "0 auto", width: "100%" }}>
          {renderContent()}
        </main>
      </div>

      {/* Lesson View Overlay */}
      {selectedLesson && (
        <div style={{ position: "fixed", inset: 0, background: "white", zIndex: 9999, display: "flex", flexDirection: "column" }}>
          <div style={{ padding: "16px 24px", borderBottom: "1px solid #E5E7EB", display: "flex", alignItems: "center", gap: 16 }}>
            <button onClick={() => setSelectedLesson(null)} style={{ padding: "8px 12px", background: "#F3F4F6", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 600 }}>← Back</button>
            <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>{selectedLesson.title}</h2>
          </div>
          <div style={{ flex: 1, padding: 24, overflowY: "auto" }}>
            <div dangerouslySetInnerHTML={{ __html: selectedLesson.content }} />
          </div>
          <div style={{ padding: 24, borderTop: "1px solid #E5E7EB", textAlign: "center" }}>
            <button onClick={() => setSelectedLesson(null)} style={{ padding: "12px 32px", background: "#3D7EFF", color: "white", border: "none", borderRadius: 12, fontSize: 16, fontWeight: 600, cursor: "pointer" }}>Finish Lesson</button>
          </div>
        </div>
      )}
    </div>
  );
}

function SidebarContent({ activeTab, setActiveTab, onBack, TABS, isDrawer = false, toggleMenu, user, userData, profile, role }) {
  const handleTabClick = (tab) => {
    setActiveTab(tab);
    if (isDrawer) toggleMenu();
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", padding: "0 0 24px" }}>
      <div style={{ padding: "24px 20px", borderBottom: "1px solid #F3F4F6", marginBottom: 16 }}> {/* Adjusted padding */}
        <div style={{ fontSize: 18, fontWeight: 800, color: "#3D7EFF", letterSpacing: -0.5 }}>LIFEWELLNESS</div> {/* Adjusted font size */}
        <div style={{ fontSize: 10, color: "#6B7280", fontWeight: 700, letterSpacing: 1, marginTop: 4 }}>STUDENT PORTAL</div> {/* Adjusted font size */}
      </div>

      <div style={{ flex: 1 }}>
        <div className={`menu-item ${activeTab === TABS.COURSES ? "active" : ""}`} onClick={() => handleTabClick(TABS.COURSES)}>
          <span style={{ fontSize: 16 }}>📚</span> My Courses {/* Adjusted icon size */}
        </div>
        <div className={`menu-item ${activeTab === TABS.SELF_LOG ? "active" : ""}`} onClick={() => handleTabClick(TABS.SELF_LOG)}>
          <span style={{ fontSize: 16 }}>📝</span> Self Log
        </div>
        <div className={`menu-item ${activeTab === TABS.PROGRESS ? "active" : ""}`} onClick={() => handleTabClick(TABS.PROGRESS)}>
          <span style={{ fontSize: 16 }}>📊</span> Log Timeline {/* Renamed from Progress Report */}
        </div>
        <div className={`menu-item ${activeTab === TABS.DIET ? "active" : ""}`} onClick={() => handleTabClick(TABS.DIET)}>
          <span style={{ fontSize: 16 }}>🍎</span> Diet Timeline {/* Renamed from Diet Analysis */}
        </div>
        <div className={`menu-item ${activeTab === TABS.AI ? "active" : ""}`} onClick={() => handleTabClick(TABS.AI)}>
          <span style={{ fontSize: 16 }}>✨</span> Life AI Assistant {/* Updated label */}
        </div>
        <div className={`menu-item ${activeTab === TABS.TYPING ? "active" : ""}`} onClick={() => handleTabClick(TABS.TYPING)}>
          <span style={{ fontSize: 16 }}>⌨️</span> Typing Tutor
        </div>
        <div className={`menu-item ${activeTab === TABS.PROFILE ? "active" : ""}`} onClick={() => handleTabClick(TABS.PROFILE)}>
          <span style={{ fontSize: 16 }}>👤</span> My Profile
        </div>
      </div>

      <div style={{ padding: "12px", borderTop: "1px solid #F3F4F6", marginTop: "auto" }}>
        {/* User Info Display */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
          {profile?.photoURL ? (
            <img src={profile.photoURL} alt="Profile" style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover", border: `1px solid ${C.border}` }} />
          ) : (
            <div style={{ width: 32, height: 32, borderRadius: "50%", background: C.accentSoft, color: C.accent, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700 }}>
              {userData?.name?.charAt(0) || "U"}
            </div>
          )}
          <div style={{ overflow: "hidden", flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.text, whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{userData?.name || "Student"}</div>
            <div style={{ fontSize: 10, color: C.accent, fontWeight: 600, textTransform: "capitalize" }}>{(role || "student").toUpperCase()}</div>
          </div>
        </div>
        <div style={{ fontSize: 10, color: C.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user?.email || "Kiosk Session"}</div>

        <div className="menu-item" onClick={onBack} style={{ color: "#DC2626", marginTop: 12 }}> {/* Added marginTop for spacing */}
          <span style={{ fontSize: 18 }}>🚪</span> Exit Session
        </div>
      </div>
    </div>
  );
}
